import React from 'react';
import { Chip, Tooltip, Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import PublicIcon from '@mui/icons-material/Public';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { 
  getStatusColor, 
  getStatusBadgeText, 
  getStatusMessage,
  formatScrapingDate,
  daysSince,
  needsUpdate
} from 'app/utils/scrapingUtils';

/**
 * Badge de status de scraping do processo
 * Exibe informações sobre a completude e fonte dos dados
 */
const ScrapingStatusBadge = ({ 
  is_scraping_completo, 
  fonte_scraping, 
  data_ultimo_scraping,
  size = 'small',
  showTooltip = true 
}) => {
  const color = getStatusColor(is_scraping_completo);
  const label = getStatusBadgeText(is_scraping_completo, fonte_scraping);
  
  // Determina o ícone baseado no status e fonte
  const getIcon = () => {
    if (is_scraping_completo === null) {
      switch (fonte_scraping) {
        case 'PDF': return <DescriptionIcon fontSize="small" />;
        case 'RPI_XML': return <NewspaperIcon fontSize="small" />;
        default: return <InfoIcon fontSize="small" />;
      }
    }
    
    if (is_scraping_completo) {
      return <CheckCircleIcon fontSize="small" />;
    }
    
    return <WarningIcon fontSize="small" />;
  };

  // Constrói o texto do tooltip
  const getTooltipContent = () => {
    const processo = { is_scraping_completo, fonte_scraping, data_ultimo_scraping };
    const statusMsg = getStatusMessage(processo);
    const lastUpdate = formatScrapingDate(data_ultimo_scraping);
    const days = data_ultimo_scraping ? daysSince(data_ultimo_scraping) : null;
    const needsSync = needsUpdate(processo);
    
    return (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Status dos Dados
        </Typography>
        <Typography variant="caption" display="block">
          {statusMsg}
        </Typography>
        {fonte_scraping && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Fonte: {fonte_scraping}
          </Typography>
        )}
        {data_ultimo_scraping && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Última atualização: {lastUpdate}
          </Typography>
        )}
        {needsSync && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'warning.main' }}>
            ⚠️ Dados podem estar desatualizados
          </Typography>
        )}
      </Box>
    );
  };

  const badge = (
    <Chip
      icon={getIcon()}
      label={label}
      color={color}
      size={size}
      variant={is_scraping_completo === false ? 'outlined' : 'filled'}
      sx={{
        fontWeight: 500,
        fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
      }}
    />
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <Tooltip title={getTooltipContent()} arrow placement="top">
      {badge}
    </Tooltip>
  );
};

export default ScrapingStatusBadge;
